<template>
  <v-container fluid class="pa-0">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card elevation="2" class="pa-4 d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4">
            <v-icon dark>mdi-receipt</v-icon>
          </v-avatar>
          <div>
            <h2 class="mb-1">Receipt Settings</h2>
            <div class="subtitle-1 grey--text">Customize receipts for each store and type</div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Store and Receipt Selection -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedStoreId"
          :items="stores"
          item-text="name"
          item-value="id"
          label="Select Store"
          prepend-icon="mdi-store"
          :rules="[rules.required]"
          required
        />
      </v-col>
      <v-col cols="12" md="8">
        <v-card flat class="pa-2 d-flex align-center flex-wrap">
          <div class="mr-4 font-weight-bold">Receipts:</div>
          <v-chip
            v-for="receipt in filteredReceipts"
            :key="receipt.id"
            :color="receipt.active ? 'primary' : 'grey'"
            class="mr-2 mb-2"
            :input-value="receipt.id === selectedReceiptId"
            @click="selectReceipt(receipt.id)"
            outlined
            label
          >
            <v-icon left small>{{ receipt.active ? 'mdi-check-circle' : 'mdi-cancel' }}</v-icon>
            {{ receipt.title }}
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="toggleReceiptActive(receipt)">
                  <v-list-item-icon>
                    <v-icon>{{ receipt.active ? 'mdi-close-circle' : 'mdi-check-circle' }}</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>{{ receipt.active ? 'Deactivate' : 'Activate' }}</v-list-item-title>
                </v-list-item>
                <v-list-item @click="openDeleteDialog(receipt)">
                  <v-list-item-icon>
                    <v-icon color="red">mdi-delete</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-chip>
          <v-btn color="success" small class="ml-2" @click="showCreateDialog = true">
            <v-icon left small>mdi-plus</v-icon> New Receipt
          </v-btn>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <!-- Settings Form -->
      <v-col cols="12" md="7">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-expansion-panels multiple>
            <!-- Printing Options -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left class="mr-2">mdi-printer</v-icon> Printing Options
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.autoPrintOnSale"
                      label="Auto Print Receipt on Sale"
                      @change="updateAutoPrintSetting('autoPrintOnSale', settings.autoPrintOnSale)"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.autoPrintOnOrder"
                      label="Auto Print Receipt on Order Creation"
                      @change="updateAutoPrintSetting('autoPrintOnOrder', settings.autoPrintOnOrder)"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.autoPrintOnOrderUpdate"
                      label="Auto Print Receipt on Order Update"
                      @change="updateAutoPrintSetting('autoPrintOnOrderUpdate', settings.autoPrintOnOrderUpdate)"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- General Settings -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left class="mr-2">mdi-cog</v-icon> General
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="settings.settings.title"
                      label="Receipt Title"
                      :rules="[rules.required]"
                      prepend-icon="mdi-format-title"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="settings.settings.paperSize"
                      :items="paperSizes"
                      label="Paper Size"
                      prepend-icon="mdi-printer"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-file-input
                      v-model="logoFile"
                      label="Logo Upload"
                      prepend-icon="mdi-image"
                      accept="image/*"
                      show-size
                      @change="onLogoChange"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="settings.settings.footer"
                      label="Footer Message"
                      prepend-icon="mdi-text"
                      hint="Shown at the bottom of the receipt"
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="settings.settings.language"
                      :items="languages"
                      label="Language"
                      prepend-icon="mdi-translate"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Business Info -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left class="mr-2">mdi-store</v-icon> Business Info
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="settings.settings.businessName"
                      label="Business Name"
                      prepend-icon="mdi-domain"
                      :rules="[rules.required]"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="settings.settings.businessAddress"
                      label="Address"
                      prepend-icon="mdi-map-marker"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="settings.settings.businessPhone"
                      label="Phone Number"
                      prepend-icon="mdi-phone"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="settings.settings.taxId"
                      label="Tax ID"
                      prepend-icon="mdi-numeric"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Display Options -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left class="mr-2">mdi-eye-settings</v-icon> Display Options
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.settings.showCashier"
                      label="Show Cashier Name"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.settings.showCustomer"
                      label="Show Customer Info"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.settings.showTaxBreakdown"
                      label="Show Tax Breakdown"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.settings.showBarcode"
                      label="Show Barcode"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-switch
                      v-model="settings.settings.showQRCode"
                      label="Show QR Code"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Numbering -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left class="mr-2">mdi-pound</v-icon> Numbering
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="settings.settings.prefix"
                      label="Prefix"
                      prepend-icon="mdi-alpha-p-box"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="settings.settings.nextNumber"
                      label="Next Number"
                      type="number"
                      prepend-icon="mdi-numeric"
                      :rules="[rules.required, rules.positive]"
                      required
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-btn color="warning" @click="resetNumbering" outlined>
                      <v-icon left>mdi-refresh</v-icon> Reset Numbering
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Advanced -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <v-icon left class="mr-2">mdi-tune-variant</v-icon> Advanced
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="settings.settings.customMessage"
                      label="Custom Message"
                      prepend-icon="mdi-message-text"
                      hint="This message will appear on all receipts."
                      persistent-hint
                      rows="2"
                    />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <!-- Actions -->
          <v-row class="mt-6">
            <v-col cols="12" class="d-flex justify-end">
              <v-btn color="primary" :loading="loading" @click="saveSettings" :disabled="!valid">
                <v-icon left>mdi-content-save</v-icon> Save
              </v-btn>
              <v-btn color="secondary" class="ml-2" @click="resetForm">
                <v-icon left>mdi-undo</v-icon> Reset
              </v-btn>
              <v-btn color="success" class="ml-2" @click="testPrint">
                <v-icon left>mdi-printer-check</v-icon> Test Print
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-col>

      <!-- Live Preview -->
      <v-col cols="12" md="5">
        <v-card elevation="2" class="pa-4">
          <div class="d-flex align-center mb-2">
            <v-icon color="primary" class="mr-2">mdi-eye</v-icon>
            <span class="subtitle-2 font-weight-bold">Live Preview</span>
          </div>
          <div class="receipt-preview pa-4">
            <div class="text-center mb-2">
              <img v-if="logoUrl" :src="logoUrl" alt="Logo" style="max-height: 60px; max-width: 100px;" class="mb-2" />
              <div class="headline font-weight-bold">{{ (settings.settings && settings.settings.title) || 'Receipt Title' }}</div>
              <div class="caption">{{ (settings.settings && settings.settings.businessName) || 'Business Name' }}</div>
              <div class="caption">{{ (settings.settings && settings.settings.businessAddress) }}</div>
              <div class="caption">{{ (settings.settings && settings.settings.businessPhone) }}</div>
              <div class="caption">{{ (settings.settings && settings.settings.taxId) ? 'Tax ID: ' + (settings.settings && settings.settings.taxId) : '' }}</div>
            </div>
            <v-divider class="my-2" />
            <div class="d-flex justify-space-between">
              <span>Receipt No:</span>
              <span>{{ (settings.settings && settings.settings.prefix) }}{{ (settings.settings && settings.settings.nextNumber) }}</span>
            </div>
            <div v-if="settings.settings.showCashier" class="d-flex justify-space-between">
              <span>Cashier:</span>
              <span>John Doe</span>
            </div>
            <div v-if="settings.settings.showCustomer" class="d-flex justify-space-between">
              <span>Customer:</span>
              <span>Jane Smith</span>
            </div>
            <div v-if="settings.settings.showTaxBreakdown" class="d-flex justify-space-between">
              <span>Tax:</span>
              <span>$2.00</span>
            </div>
            <div class="d-flex justify-space-between">
              <span>Total:</span>
              <span>$20.00</span>
            </div>
            <div v-if="settings.settings.showBarcode" class="text-center mt-2">
              <v-icon color="grey">mdi-barcode</v-icon>
            </div>
            <div v-if="settings.settings.showQRCode" class="text-center mt-2">
              <v-icon color="grey">mdi-qrcode</v-icon>
            </div>
            <v-divider class="my-2" />
            <div class="text-center caption">
              {{ (settings.settings && settings.settings.footer) || 'Thank you for your purchase!' }}
            </div>
            <div v-if="settings.settings.customMessage" class="text-center mt-2 caption">
              {{ (settings.settings && settings.settings.customMessage) }}
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Create Receipt Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="400">
      <v-card>
        <v-card-title>
          <v-icon left class="mr-2">mdi-plus</v-icon> New Receipt
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="newReceiptTitle" label="Receipt Title" :rules="[rules.required]" required />
          <v-select v-model="newReceiptLanguage" :items="languages" label="Language" :rules="[rules.required]" required />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showCreateDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="createReceipt" :disabled="!newReceiptTitle || !newReceiptLanguage">Create</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Receipt Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title>
          <v-icon left color="red" class="mr-2">mdi-delete</v-icon> Delete Receipt
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete the receipt <b>{{ receiptToDelete?.title }}</b>?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="red" @click="deleteReceipt">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import SettingService from '@/services/setting.service'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin'

export default {
  name: 'SalesReceipts',
  mixins: [ErrorHandlingMixin],
  data () {
    return {
      valid: false,
      loading: false,
      stores: [],
      selectedStoreId: null,
      receipts: [],
      selectedReceiptId: null,
      settings: {
        settings: {
          title: '',
          footer: '',
          logo: null,
          paperSize: '',
          businessName: '',
          businessAddress: '',
          businessPhone: '',
          taxId: '',
          showCashier: true,
          showCustomer: true,
          showTaxBreakdown: true,
          showBarcode: false,
          showQRCode: false,
          prefix: 'RCPT-',
          nextNumber: 1001,
          customMessage: '',
          language: 'en'
        },
        autoPrintOnSale: false,
        autoPrintOnOrder: false,
        autoPrintOnOrderUpdate: false
      },
      paperSizes: [
        { text: '80mm', value: '80mm' },
        { text: '58mm', value: '58mm' },
        { text: 'A4', value: 'A4' }
      ],
      languages: [
        { text: 'English', value: 'en' },
        { text: 'French', value: 'fr' }
      ],
      rules: {
        required: v => !!v || 'Required',
        positive: v => (v > 0) || 'Must be positive'
      },
      // Dialogs
      showCreateDialog: false,
      newReceiptTitle: '',
      newReceiptLanguage: '',
      showDeleteDialog: false,
      receiptToDelete: null,
      logoUrl: '',
      logoFile: null
    }
  },
  computed: {
    filteredReceipts () {
      return this.receipts.filter(r => r.store_id === this.selectedStoreId)
    },
    selectedReceipt () {
      return this.receipts.find(r => r.id === this.selectedReceiptId) || {}
    }
  },
  watch: {
    selectedStoreId (val) {
      if (val) {
        this.fetchStoreReceiptSettings(val)
        this.fetchStoreReceipts(val)
      }
    },
    selectedReceiptId (val) {
      // No need to fetch settings, as settings are store-wide
      // Only update preview title
    }
  },
  methods: {
    async fetchStores () {
      await this.withLoading(async () => {
        // You may have a storeService or use $store.state.Auth.user.stores
        // For now, assume an API endpoint exists:
        const stores = await this.$storeService.getStores()
        this.stores = stores
        if (!this.selectedStoreId && stores.length) {
          this.selectedStoreId = stores[0].id
        }
      }, 'loading')
    },
    async fetchStoreReceiptSettings (storeId) {
      await this.withLoading(async () => {
        this.settings = await this.$settingService.getStoreReceiptSettings(storeId)
        // If logo is a URL, set logoUrl but keep logoFile as null
        if (this.settings.settings && this.settings.settings.logo) {
          this.logoUrl = this.settings.settings.logo
        } else {
          this.logoUrl = ''
        }
        // Reset logoFile to null since we're loading from server
        this.logoFile = null
      }, 'loading')
    },
    async fetchStoreReceipts (storeId) {
      await this.withLoading(async () => {
        const receipts = await this.$settingService.getStoreReceipts(storeId)
        this.receipts = receipts
        if (receipts.length) {
          this.selectedReceiptId = receipts[0].id
        } else {
          this.selectedReceiptId = null
        }
      }, 'loading')
    },
    selectReceipt (id) {
      this.selectedReceiptId = id
    },
    onLogoChange (file) {
      if (file && file instanceof File) {
        const reader = new FileReader()
        reader.onload = e => {
          this.logoUrl = e.target.result
          this.settings.settings.logo = e.target.result
        }
        reader.onerror = () => {
          console.error('Error reading file')
          this.logoUrl = ''
          this.settings.settings.logo = null
        }
        reader.readAsDataURL(file)
      } else {
        this.logoUrl = ''
        this.settings.settings.logo = null
      }
    },
    async saveSettings () {
      await this.withLoading(async () => {
        await this.$settingService.updateStoreReceiptSettings(this.selectedStoreId, {
          settings: this.settings.settings,
          autoPrintOnSale: this.settings.autoPrintOnSale,
          autoPrintOnOrder: this.settings.autoPrintOnOrder,
          autoPrintOnOrderUpdate: this.settings.autoPrintOnOrderUpdate
        })
        this.showSuccess('Receipt settings saved successfully!')
      }, 'loading')
    },
    async resetForm () {
      await this.fetchStoreReceiptSettings(this.selectedStoreId)
      this.logoFile = null
      this.showInfo('Form reset to saved values.')
    },
    async resetNumbering () {
      this.settings.settings.nextNumber = 1
      await this.saveSettings()
      this.showInfo('Receipt numbering reset to 1.')
    },
    async testPrint () {
      this.showSuccess('Test print sent (simulated).')
    },
    async toggleReceiptActive (receipt) {
      await this.withLoading(async () => {
        await this.$settingService.toggleStoreReceiptActive(receipt.id)
        await this.fetchStoreReceipts(this.selectedStoreId)
        this.showSuccess(receipt.active ? 'Receipt deactivated.' : 'Receipt activated.')
      }, 'loading')
    },
    openDeleteDialog (receipt) {
      this.receiptToDelete = receipt
      this.showDeleteDialog = true
    },
    async deleteReceipt () {
      await this.withLoading(async () => {
        await this.$settingService.deleteStoreReceipt(this.receiptToDelete.id)
        await this.fetchStoreReceipts(this.selectedStoreId)
        this.showDeleteDialog = false
        this.showInfo('Receipt deleted.')
      }, 'loading')
    },
    async createReceipt () {
      await this.withLoading(async () => {
        await this.$settingService.createStoreReceipt(this.selectedStoreId, {
          title: this.newReceiptTitle,
          language: this.newReceiptLanguage,
          active: true
        })
        await this.fetchStoreReceipts(this.selectedStoreId)
        this.showCreateDialog = false
        this.newReceiptTitle = ''
        this.newReceiptLanguage = ''
        this.showSuccess('New receipt created.')
      }, 'loading')
    },
    // For toggles in settings
    async updateAutoPrintSetting (key, value) {
      this.settings[key] = value
      await this.saveSettings()
    }
  },
  async mounted () {
    this.$settingService = new SettingService(this.$http, this.$store, this.$router)
    await this.fetchStores()
  }
}
</script>

<style scoped>
.receipt-preview {
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #eee;
  min-height: 350px;
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 15px;
}
</style>
